<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-split class="page-split__referrals">
                <template v-if="!isMobileLarge" #aside>
                    <referrals-show-details
                        :referral="referral"
                        :type="$route.params.type"
                        @referral-changed="changeReferralData($event)"
                    ></referrals-show-details>
                </template>

                <template #main>
                    <div
                        v-bind="contentProps"
                        @referral-changed="changeReferralData($event)"
                        @insurance-auth-declined="changeReferralData({ isInsuranceJustDeclined: true })"
                    ></div>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ReferralsShowDetails from 'components/page-elements/referrals/ReferralsShowDetails'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralStatusMixin, ScreenSizeDetectionMixin],

        components: {
            AppLayout,
            ReferralsShowDetails,
        },

        data() {
            return {
                referral: {},
            }
        },

        computed: {
            isReferred() {
                return this.$route.params.type === ReferralPathTypeEnum.RECEIVED
            },

            contentProps() {
                if (
                    this.referral.status === 'CREATED' ||
                    this.referral.status === 'RESCHEDULE_REQUESTED' ||
                    this.referral.status === 'RESCHEDULE_AVAILABILITY_CHANGE' ||
                    (this.isReferralError(this.referral) && !this.isReferred) ||
                    (this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING &&
                        this.$route.query.successfully_rescheduled == 1)
                ) {
                    return {
                        is: 'referrals-show-pending',
                        referral: this.referral,
                    }
                } else if (
                    this.referral.status === ReferralStatusEnum.PATIENT_VISIT_PENDING ||
                    this.referral.status === 'CONSULTATION_NOTES_PENDING' ||
                    this.referral.status === 'INSURANCE_AUTH_PENDING' ||
                    this.referral.status === 'COMPLETED' ||
                    (this.isReferralError(this.referral) && this.isReferred)
                ) {
                    return {
                        is: 'referrals-view-details',
                        referral: this.referral,
                    }
                }

                return null
            },

            physicianName() {
                return composeDisplayName(
                    this.referral[this.$route.params.type === this.isReferred ? 'toUser' : 'fromUser'] || {},
                )
            },

            pageTitle() {
                return this.$t(`pages.referrals.show.titles.${this.$route.params.type}`, {
                    name: this.getReferralTypeName,
                    designation: this.referral?.primaryCarePhysician?.designation || '',
                })
            },

            getReferralTypeName() {
                if (this.$route.params.type === this.isReferred) {
                    if (this.referral.fromPractice?.practiceType === PracticeTypeEnum.PHYSICIAN) {
                        return this.physicianName
                    } else {
                        return this.referral.fromPractice?.name
                    }
                } else {
                    if (this.referral.practice && this.referral.practice.practiceType === PracticeTypeEnum.PHYSICIAN) {
                        return this.physicianName
                    } else if (this.referral.practice && this.referral.practice.practiceType === PracticeTypeEnum.ANCILLARY) {
                        return this.referral.practice.name
                    } else {
                        return ''
                    }
                }
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`),
                        route: { name: `referrals.${this.$route.params.type}` },
                    },
                    {
                        label: this.$t(`pages.referrals.show.breadcrumbs.${this.$route.params.type}`, {
                            name: this.getReferralTypeName,
                        }),
                    },
                ]
            },
        },

        methods: {
            changeReferralData(data) {
                this.referral = { ...this.referral, ...data }
            },
        },
        watch: {
            $route() {
                ReferralsAPI.get(
                    this.$route.params.id,
                    this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
                )
                    .then((res) => (this.referral = res))
                    .catch(() => {
                        this.$router.go(-1)
                    })
            },
        },
        mounted() {
            ReferralsAPI.get(
                this.$route.params.id,
                this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id,
            )
                .then((res) => (this.referral = res))
                .catch(() => {
                    this.$router.go(-1)
                })
        },
    }
</script>
